import React, { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import BookmarkBorder from "@mui/icons-material/BookmarkBorder";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import { readSavedViews, SAVED_VIEWS_KEY } from "./auditLogUtils";

export default function SavedLogViews({ filters, onApply, notify, disabled }) {
  const [views, setViews] = useState(readSavedViews);
  const [anchor, setAnchor] = useState(null);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState("");
  const duplicate = views.some(
    (view) => view.name.toLowerCase() === name.trim().toLowerCase(),
  );
  const persist = (next) => {
    try {
      localStorage.setItem(SAVED_VIEWS_KEY, JSON.stringify(next));
      setViews(next);
      return true;
    } catch {
      notify(
        "Could not save views. Browser storage may be unavailable or full.",
        "error",
      );
      return false;
    }
  };
  const save = (event) => {
    event.preventDefault();
    if (!name.trim() || duplicate || views.length >= 20) return;
    if (
      persist([
        ...views,
        {
          id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
          name: name.trim(),
          filters: { ...filters, page: 1 },
        },
      ])
    ) {
      setSaving(false);
      notify("View saved in this browser.");
    }
  };
  return (
    <>
      <Button
        variant="outlined"
        startIcon={<BookmarkBorder />}
        onClick={(event) => setAnchor(event.currentTarget)}
      >
        Saved views
      </Button>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        PaperProps={{
          sx: { width: 310, maxWidth: "calc(100vw - 32px)", borderRadius: 2 },
        }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle2">Saved views</Typography>
          <Typography variant="caption" color="text.secondary">
            Stored only in this browser · {views.length}/20
          </Typography>
        </Box>
        {views.length === 0 && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ px: 2, py: 2 }}
          >
            Save a query you use often.
          </Typography>
        )}
        {views.map((view) => (
          <Stack
            key={view.id}
            direction="row"
            alignItems="center"
            sx={{ pr: 1 }}
          >
            <MenuItem
              sx={{ flex: 1, minWidth: 0 }}
              onClick={() => {
                setAnchor(null);
                onApply(view.filters);
              }}
            >
              <Typography variant="body2" noWrap>
                {view.name}
              </Typography>
            </MenuItem>
            <Tooltip title={`Delete ${view.name}`}>
              <IconButton
                size="small"
                aria-label={`Delete saved view ${view.name}`}
                onClick={() =>
                  persist(views.filter((item) => item.id !== view.id))
                }
              >
                <DeleteOutline fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        ))}
        <MenuItem
          disabled={disabled || views.length >= 20}
          onClick={() => {
            setAnchor(null);
            setName("");
            setSaving(true);
          }}
          sx={{
            color: "primary.main",
            borderTop: 1,
            borderColor: "divider",
            mt: 1,
          }}
        >
          Save displayed query…
        </MenuItem>
      </Menu>
      <Dialog
        open={saving}
        onClose={() => setSaving(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ component: "form", onSubmit: save }}
      >
        <DialogTitle>Save this view</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Save the filters for the displayed results. Relative time ranges
            update whenever you open the view.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            label="View name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            inputProps={{ maxLength: 60 }}
            error={duplicate}
            helperText={
              duplicate
                ? "A view with this name already exists."
                : "For example: Failed sign-ins"
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaving(false)}>Cancel</Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!name.trim() || duplicate || views.length >= 20}
          >
            Save view
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
